/**
 * Phase 4.5 — troop types, train/promote/demote graph, costs, demote floor.
 *
 * SoT: docs/FUTURE_TROOP_TYPES_DESIGN.md §3.1–3.2
 *      docs/FUTURE_PHASE_4_5_EXPANDED.md §2
 * Plan: .hermes/plans/phase-4.5-implementation.md
 *
 * Levy (able → MAA) stays in call.ts — this module is the factory graph only.
 */

import type { ResourceId, StockMap } from "./economy.ts";
import { clampStock } from "./economy.ts";

// ── Types ───────────────────────────────────────────────────────────────────

export type TroopTypeId =
  | "maa"
  | "foot"
  | "pike"
  | "archer"
  | "cavalry"
  | "catapult"
  | "guard"
  | "knight";

/** Matrix / peel order (clay first, elites last; catapult near end). */
export const TROOP_TYPES: readonly TroopTypeId[] = [
  "maa",
  "foot",
  "pike",
  "archer",
  "cavalry",
  "guard",
  "knight",
  "catapult",
] as const;

export const TROOP_LABELS: Record<TroopTypeId, string> = {
  maa: "Men-at-arms",
  foot: "Footmen",
  pike: "Pikemen",
  archer: "Archers",
  cavalry: "Cavalry",
  catapult: "Catapults",
  guard: "Personal guard",
  knight: "Knights",
};

export type TroopStacks = Record<TroopTypeId, number>;

/** Resource channels that can appear on train/promote edges. */
export type TroopCost = Partial<Record<ResourceId, number>>;

export type TrainEdgeId =
  | "maa_foot"
  | "maa_pike"
  | "maa_archer"
  | "maa_cavalry"
  | "maa_catapult"
  | "foot_guard"
  | "cavalry_knight";

export interface TrainEdge {
  id: TrainEdgeId;
  from: TroopTypeId;
  to: TroopTypeId;
  /** Cost basis per unit (also demote refund basis). */
  cost: TroopCost;
  /**
   * Extra men-at-arms consumed as crew per output unit (catapult = 4).
   * Total MAA peeled = fromPer * qty + crewMaa * qty.
   * Crew returns 100% as MAA on demote; resource refund stays ⌊×0.5⌋.
   * @deprecated Prefer crewMaa — kept as alias during rename.
   */
  crewAble: number;
  /** Preferred name for crewMaa (same value as crewAble). */
  crewMaa: number;
  /** Units of `from` consumed per 1 unit of `to` (default 1). */
  fromPer: number;
}

/** Binding starter costs — CHANGELOG must list these on 0.4.5 ship. */
export const TRAIN_EDGES: readonly TrainEdge[] = [
  {
    id: "maa_foot",
    from: "maa",
    to: "foot",
    cost: { iron: 2 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
  {
    id: "maa_pike",
    from: "maa",
    to: "pike",
    cost: { wood: 2 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
  {
    id: "maa_archer",
    from: "maa",
    to: "archer",
    cost: { wood: 2 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
  {
    id: "maa_cavalry",
    from: "maa",
    to: "cavalry",
    cost: { iron: 2, horses: 2 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
  {
    id: "maa_catapult",
    from: "maa",
    to: "catapult",
    cost: { wood: 8, iron: 4 },
    // 1 MAA chassis + 4 MAA crew = 5 MAA total per engine
    crewAble: 4,
    crewMaa: 4,
    fromPer: 1,
  },
  {
    id: "foot_guard",
    from: "foot",
    to: "guard",
    cost: { iron: 3, gold: 2 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
  {
    id: "cavalry_knight",
    from: "cavalry",
    to: "knight",
    cost: { gold: 3, iron: 2, horses: 1 },
    crewAble: 0,
    crewMaa: 0,
    fromPer: 1,
  },
] as const;

const EDGE_BY_ID = Object.fromEntries(
  TRAIN_EDGES.map((e) => [e.id, e]),
) as Record<TrainEdgeId, TrainEdge>;

const EDGE_BY_PAIR = new Map<string, TrainEdge>(
  TRAIN_EDGES.map((e) => [`${e.from}->${e.to}`, e]),
);

// ── Stacks ──────────────────────────────────────────────────────────────────

export function emptyStacks(): TroopStacks {
  return {
    maa: 0,
    foot: 0,
    pike: 0,
    archer: 0,
    cavalry: 0,
    catapult: 0,
    guard: 0,
    knight: 0,
  };
}

export function cloneStacks(s: TroopStacks): TroopStacks {
  return { ...s };
}

/** Headcount for food/supply/totals. Catapult counts as 1 engine (crew tracked via able bind). */
export function stacksTotal(s: TroopStacks | null | undefined): number {
  if (!s) return 0;
  let n = 0;
  for (const id of TROOP_TYPES) {
    n += Math.max(0, Math.floor(s[id] ?? 0));
  }
  return n;
}

export function stacksFromLegacy(n: number): TroopStacks {
  const s = emptyStacks();
  s.maa = Math.max(0, Math.floor(n));
  return s;
}

/** Duck-type: plain number → MAA stack; partial object → fill zeros. */
export function normalizeStacks(raw: unknown): TroopStacks {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return stacksFromLegacy(raw);
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const s = emptyStacks();
    for (const id of TROOP_TYPES) {
      const v = o[id];
      s[id] =
        typeof v === "number" && Number.isFinite(v)
          ? Math.max(0, Math.floor(v))
          : 0;
    }
    // Legacy mistaken shape { garrison: N } should not land here — caller handles ArmsState.
    return s;
  }
  return emptyStacks();
}

export function addToStack(
  s: TroopStacks,
  type: TroopTypeId,
  delta: number,
): TroopStacks {
  const next = cloneStacks(s);
  next[type] = Math.max(0, Math.floor((next[type] ?? 0) + delta));
  return next;
}

/**
 * Peel up to `need` heads from stacks (immutable).
 * Order: TROOP_TYPES (maa first … catapult last).
 * Returns left stacks + how many actually taken.
 */
export function peelFromStacks(
  s: TroopStacks,
  need: number,
): { left: TroopStacks; taken: number; takenStacks: TroopStacks } {
  let remain = Math.max(0, Math.round(need));
  const left = cloneStacks(s);
  const takenStacks = emptyStacks();
  let taken = 0;
  for (const id of TROOP_TYPES) {
    if (remain <= 0) break;
    const have = Math.max(0, Math.floor(left[id] ?? 0));
    const peel = Math.min(have, remain);
    if (peel <= 0) continue;
    left[id] = have - peel;
    takenStacks[id] = peel;
    taken += peel;
    remain -= peel;
  }
  return { left, taken, takenStacks };
}

/** Move `qty` of `type` from `from` into `to` (immutable). Clamps to available. */
export function moveStackType(
  from: TroopStacks,
  to: TroopStacks,
  type: TroopTypeId,
  qty: number,
): { from: TroopStacks; to: TroopStacks; moved: number } {
  const want = Math.max(0, Math.floor(qty));
  const have = Math.max(0, Math.floor(from[type] ?? 0));
  const moved = Math.min(want, have);
  return {
    from: addToStack(from, type, -moved),
    to: addToStack(to, type, moved),
    moved,
  };
}

// ── Arms helpers (typed garrison / host) ────────────────────────────────────

export interface TypedArms {
  garrison: TroopStacks;
  host: TroopStacks;
}

export function emptyArms(): TypedArms {
  return { garrison: emptyStacks(), host: emptyStacks() };
}

export function armsFromLegacy(garrison: number, host: number): TypedArms {
  return {
    garrison: stacksFromLegacy(garrison),
    host: stacksFromLegacy(host),
  };
}

/**
 * Normalize ArmsState-like input:
 * - { garrison: number, host: number } legacy
 * - { garrison: TroopStacks, host: TroopStacks } typed
 * - partial / missing → zeros
 */
export function normalizeArms(raw: unknown): TypedArms {
  if (!raw || typeof raw !== "object") return emptyArms();
  const o = raw as Record<string, unknown>;
  const g = o.garrison;
  const h = o.host;
  return {
    garrison:
      typeof g === "number" ? stacksFromLegacy(g) : normalizeStacks(g),
    host: typeof h === "number" ? stacksFromLegacy(h) : normalizeStacks(h),
  };
}

export function garrisonTotal(arms: TypedArms): number {
  return stacksTotal(arms.garrison);
}

export function hostTotal(arms: TypedArms): number {
  return stacksTotal(arms.host);
}

/** Engines in garrison + host. */
export function catapultCount(arms: TypedArms): number {
  return (
    Math.max(0, Math.floor(arms.garrison.catapult ?? 0)) +
    Math.max(0, Math.floor(arms.host.catapult ?? 0))
  );
}

/**
 * Binding playtest residual: catapult crew = exactly 4 **men-at-arms** per engine
 * (not free able-bodied). Total MAA peeled on train = fromPer + crewMaa = 5.
 */
export const CATAPULT_CREW_PER = 4;

/** MAA crew currently bound into catapult stacks (garrison + host). */
export function catapultCrewBound(arms: TypedArms): number {
  return catapultCount(arms) * CATAPULT_CREW_PER;
}

/** Crew MAA for an edge (prefers crewMaa, falls back to crewAble). */
export function edgeCrewMaa(edge: TrainEdge): number {
  const n = edge.crewMaa ?? edge.crewAble ?? 0;
  return Math.max(0, Math.floor(n));
}

/** `from` units consumed per 1 output unit. */
export function edgeFromPer(edge: TrainEdge): number {
  const n = edge.fromPer ?? 1;
  return Math.max(1, Math.floor(n));
}

/**
 * Total units of `edge.from` peeled to produce `qty` of `edge.to`.
 * Catapult: fromPer(1) + crewMaa(4) = 5 MAA per engine.
 */
export function edgeFromNeed(edge: TrainEdge, qty: number): number {
  const q = Math.max(0, Math.floor(qty));
  return (edgeFromPer(edge) + edgeCrewMaa(edge)) * q;
}

// ── Costs & demote floor ────────────────────────────────────────────────────

/** Binding: refund = ⌊cost_basis × 0.5⌋ per channel. */
export function demoteRefund(cost: TroopCost): TroopCost {
  const out: TroopCost = {};
  for (const [k, v] of Object.entries(cost)) {
    if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) continue;
    const r = Math.floor(v * 0.5);
    if (r > 0) out[k as ResourceId] = r;
  }
  return out;
}

export function edgeById(id: TrainEdgeId): TrainEdge {
  return EDGE_BY_ID[id];
}

export function edgeForPair(
  from: TroopTypeId,
  to: TroopTypeId,
): TrainEdge | undefined {
  return EDGE_BY_PAIR.get(`${from}->${to}`);
}

export function promoteEdgesFrom(from: TroopTypeId): TrainEdge[] {
  return TRAIN_EDGES.filter((e) => e.from === from);
}

export function demoteEdgesTo(from: TroopTypeId): TrainEdge[] {
  // demote: reverse edges where edge.to === current type
  return TRAIN_EDGES.filter((e) => e.to === from);
}

export function scaleCost(cost: TroopCost, qty: number): TroopCost {
  const q = Math.max(0, Math.floor(qty));
  const out: TroopCost = {};
  for (const [k, v] of Object.entries(cost)) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    out[k as ResourceId] = Math.floor(v) * q;
  }
  return out;
}

export function canAffordStocks(stocks: StockMap, cost: TroopCost): boolean {
  for (const [k, v] of Object.entries(cost)) {
    if (typeof v !== "number" || v <= 0) continue;
    const have = stocks[k as ResourceId] ?? 0;
    if (have < v) return false;
  }
  return true;
}

export function spendStocks(stocks: StockMap, cost: TroopCost): StockMap {
  const next: StockMap = { ...stocks };
  for (const [k, v] of Object.entries(cost)) {
    if (typeof v !== "number" || v <= 0) continue;
    const id = k as ResourceId;
    next[id] = clampStock((next[id] ?? 0) - v);
  }
  return next;
}

export function creditStocks(stocks: StockMap, credit: TroopCost): StockMap {
  const next: StockMap = { ...stocks };
  for (const [k, v] of Object.entries(credit)) {
    if (typeof v !== "number" || v <= 0) continue;
    const id = k as ResourceId;
    next[id] = clampStock((next[id] ?? 0) + v);
  }
  return next;
}

// ── Promote / demote (pure) ─────────────────────────────────────────────────

export type PoolKind = "garrison" | "host";

export interface TroopActionOk {
  ok: true;
  arms: TypedArms;
  stocks: StockMap;
  /**
   * Legacy free-able delta. Crew is now MAA-in-stack; always 0 for train/demote.
   * Kept so main.ts / callers stay binary-compatible.
   */
  ableDelta: number;
  /** Total `from` units peeled (fromPer*qty + crewMaa*qty). */
  fromConsumed: number;
  edge: TrainEdge;
  qty: number;
  cost: TroopCost;
  refund?: TroopCost;
  log: string;
}

export interface TroopActionErr {
  ok: false;
  error: string;
}

export type TroopActionResult = TroopActionOk | TroopActionErr;

export interface PromoteInput {
  arms: TypedArms;
  stocks: StockMap;
  /**
   * @deprecated Crew no longer binds free able. Kept optional for call-site
   * compatibility; ignored by promoteTroops.
   */
  freeAble?: number;
  edgeId: TrainEdgeId;
  qty: number;
  pool?: PoolKind;
}

export function promoteTroops(input: PromoteInput): TroopActionResult {
  const edge = EDGE_BY_ID[input.edgeId];
  if (!edge) return { ok: false, error: "Unknown train edge." };
  const qty = Math.max(0, Math.floor(input.qty));
  if (qty <= 0) return { ok: false, error: "Quantity must be at least 1." };
  const pool = input.pool ?? "garrison";
  const stacks = pool === "garrison" ? input.arms.garrison : input.arms.host;
  const fromNeed = edgeFromNeed(edge, qty);
  const have = Math.max(0, Math.floor(stacks[edge.from] ?? 0));
  if (have < fromNeed) {
    const crew = edgeCrewMaa(edge) * qty;
    const chassis = edgeFromPer(edge) * qty;
    const crewBit =
      crew > 0
        ? ` (${chassis} ${TROOP_LABELS[edge.from]} + ${crew} crew men-at-arms)`
        : "";
    return {
      ok: false,
      error: `Need ${fromNeed} ${TROOP_LABELS[edge.from]}${crewBit}; have ${have} in ${pool}.`,
    };
  }
  const cost = scaleCost(edge.cost, qty);
  if (!canAffordStocks(input.stocks, cost)) {
    return { ok: false, error: "Not enough stocks for that train/promote." };
  }
  let nextStacks = addToStack(stacks, edge.from, -fromNeed);
  nextStacks = addToStack(nextStacks, edge.to, qty);
  const arms: TypedArms = {
    garrison:
      pool === "garrison" ? nextStacks : cloneStacks(input.arms.garrison),
    host: pool === "host" ? nextStacks : cloneStacks(input.arms.host),
  };
  const stocks = spendStocks(input.stocks, cost);
  const crew = edgeCrewMaa(edge) * qty;
  const crewNote =
    crew > 0 ? ` · crew ${crew} men-at-arms` : "";
  return {
    ok: true,
    arms,
    stocks,
    ableDelta: 0,
    fromConsumed: fromNeed,
    edge,
    qty,
    cost,
    log: `Trained ${qty} ${TROOP_LABELS[edge.to]} from ${fromNeed} ${TROOP_LABELS[edge.from]}${crewNote} (${pool}).`,
  };
}

export interface DemoteInput {
  arms: TypedArms;
  stocks: StockMap;
  /** Reverse this promote edge (to → from). */
  edgeId: TrainEdgeId;
  qty: number;
  pool?: PoolKind;
}

/**
 * Voluntary demote: reverse a train edge; refund ⌊basis×0.5⌋ per resource channel;
 * **100%** of fromPer + crewMaa men-at-arms return to the `from` stack (no free-able).
 */
export function demoteTroops(input: DemoteInput): TroopActionResult {
  const edge = EDGE_BY_ID[input.edgeId];
  if (!edge) return { ok: false, error: "Unknown train edge." };
  const qty = Math.max(0, Math.floor(input.qty));
  if (qty <= 0) return { ok: false, error: "Quantity must be at least 1." };
  const pool = input.pool ?? "garrison";
  const stacks = pool === "garrison" ? input.arms.garrison : input.arms.host;
  // Demote moves edge.to → edge.from (full fromNeed restored)
  const have = Math.max(0, Math.floor(stacks[edge.to] ?? 0));
  if (have < qty) {
    return {
      ok: false,
      error: `Need ${qty} ${TROOP_LABELS[edge.to]}; have ${have} in ${pool}.`,
    };
  }
  const basis = scaleCost(edge.cost, qty);
  const refund = demoteRefund(basis);
  const fromReturn = edgeFromNeed(edge, qty);
  let nextStacks = addToStack(stacks, edge.to, -qty);
  nextStacks = addToStack(nextStacks, edge.from, fromReturn);
  const arms: TypedArms = {
    garrison:
      pool === "garrison" ? nextStacks : cloneStacks(input.arms.garrison),
    host: pool === "host" ? nextStacks : cloneStacks(input.arms.host),
  };
  const stocks = creditStocks(input.stocks, refund);
  const crew = edgeCrewMaa(edge) * qty;
  const crewNote =
    crew > 0 ? ` · restored ${fromReturn} ${TROOP_LABELS[edge.from]} (100% crew)` : "";
  return {
    ok: true,
    arms,
    stocks,
    ableDelta: 0,
    fromConsumed: fromReturn,
    edge,
    qty,
    cost: basis,
    refund,
    log: `Demoted ${qty} ${TROOP_LABELS[edge.to]} → ${fromReturn} ${TROOP_LABELS[edge.from]}${crewNote} (${pool}); resource refund ⌊×0.5⌋.`,
  };
}

/** Preview refund lines for UI (no state change). */
export function previewDemoteRefund(
  edgeId: TrainEdgeId,
  qty: number,
): TroopCost {
  const edge = EDGE_BY_ID[edgeId];
  if (!edge) return {};
  return demoteRefund(scaleCost(edge.cost, qty));
}

/**
 * Peel troops for liege send: host first, then garrison (by stack peel order).
 * Immutable.
 */
export function peelArmsStacksForLiegeSend(
  arms: TypedArms,
  troops: number,
): TypedArms {
  return peelArmsStacksForLiegeSendDetail(arms, emptyHostOrigin(), troops).arms;
}

/**
 * Peel discrete stacks for a liege send, tracking ownership.
 * Host first, then garrison. Origin peels prefer seat, then other keys.
 * When origin ledger is short, remaining taken stacks are credited to seat.
 */
export function peelArmsStacksForLiegeSendDetail(
  arms: TypedArms,
  origin: HostOriginMap | null | undefined,
  troops: number,
): {
  arms: TypedArms;
  origin: HostOriginMap;
  takenStacks: TroopStacks;
  takenByKey: HostOriginMap;
  taken: number;
} {
  let need = Math.max(0, Math.round(troops));
  let host = cloneStacks(arms.host);
  let garrison = cloneStacks(arms.garrison);
  let takenStacks = emptyStacks();
  let taken = 0;
  if (need > 0) {
    const h = peelFromStacks(host, need);
    host = h.left;
    takenStacks = addStacks(takenStacks, h.takenStacks);
    taken += h.taken;
    need -= h.taken;
  }
  if (need > 0) {
    const g = peelFromStacks(garrison, need);
    garrison = g.left;
    takenStacks = addStacks(takenStacks, g.takenStacks);
    taken += g.taken;
    need -= g.taken;
  }
  // Ownership ledger: peel matching count (seat preferred).
  const op = peelHostOrigin(origin, taken, [HOST_ORIGIN_SEAT]);
  let takenByKey = cloneHostOrigin(op.takenByKey);
  const nextOrigin = op.origin;
  const originTaken = hostOriginTotal(takenByKey);
  if (originTaken < taken) {
    const short = taken - originTaken;
    takenByKey = creditHostOrigin(
      takenByKey,
      HOST_ORIGIN_SEAT,
      scaleStacksToTotal(takenStacks, short),
    );
  } else if (originTaken > taken && taken > 0) {
    takenByKey = scaleHostOriginToTotal(takenByKey, taken);
  }
  return {
    arms: { garrison, host },
    origin: nextOrigin,
    takenStacks,
    takenByKey,
    taken,
  };
}

/** Add scalar spears as MAA into a pool (banner arrivals, levy resolve, etc.). */
export function addMaa(
  arms: TypedArms,
  pool: PoolKind,
  n: number,
): TypedArms {
  const qty = Math.max(0, Math.floor(n));
  if (qty <= 0) return { garrison: cloneStacks(arms.garrison), host: cloneStacks(arms.host) };
  if (pool === "garrison") {
    return {
      garrison: addToStack(arms.garrison, "maa", qty),
      host: cloneStacks(arms.host),
    };
  }
  return {
    garrison: cloneStacks(arms.garrison),
    host: addToStack(arms.host, "maa", qty),
  };
}

/** Merge taken typed stacks into a destination pool. */
export function mergeStacksInto(
  arms: TypedArms,
  pool: PoolKind,
  add: TroopStacks,
): TypedArms {
  const base = pool === "garrison" ? arms.garrison : arms.host;
  const next = cloneStacks(base);
  for (const id of TROOP_TYPES) {
    next[id] = Math.max(0, Math.floor((next[id] ?? 0) + (add[id] ?? 0)));
  }
  return {
    garrison: pool === "garrison" ? next : cloneStacks(arms.garrison),
    host: pool === "host" ? next : cloneStacks(arms.host),
  };
}

/** Sum two stack bags (immutable). */
export function addStacks(a: TroopStacks, b: TroopStacks): TroopStacks {
  const next = cloneStacks(a);
  for (const id of TROOP_TYPES) {
    next[id] = Math.max(0, Math.floor((next[id] ?? 0) + (b[id] ?? 0)));
  }
  return next;
}

/**
 * Host ownership ledger: banner key → typed stacks currently in the player's host
 * that originated from that banner (or "seat" for player-owned host).
 * Binding playtest residual: type + ownership stay discrete through call/march/release.
 */
export type HostOriginMap = Record<string, TroopStacks>;

/** Canonical key for player-owned host (not a banner visitor). */
export const HOST_ORIGIN_SEAT = "seat";

export function emptyHostOrigin(): HostOriginMap {
  return {};
}

export function cloneHostOrigin(m: HostOriginMap | null | undefined): HostOriginMap {
  if (!m || typeof m !== "object") return {};
  const out: HostOriginMap = {};
  for (const [k, v] of Object.entries(m)) {
    out[k] = cloneStacks(v);
  }
  return out;
}

export function normalizeHostOrigin(raw: unknown): HostOriginMap {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: HostOriginMap = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!k) continue;
    const s = normalizeStacks(v);
    if (stacksTotal(s) > 0) out[k] = s;
  }
  return out;
}

export function hostOriginTotal(origin: HostOriginMap | null | undefined): number {
  if (!origin) return 0;
  let n = 0;
  for (const s of Object.values(origin)) n += stacksTotal(s);
  return n;
}

/** Credit typed stacks to an origin key (immutable). Drops empty keys. */
export function creditHostOrigin(
  origin: HostOriginMap | null | undefined,
  key: string,
  add: TroopStacks,
): HostOriginMap {
  const k = (key && key.trim()) || HOST_ORIGIN_SEAT;
  const out = cloneHostOrigin(origin);
  const merged = addStacks(out[k] ?? emptyStacks(), add);
  if (stacksTotal(merged) <= 0) delete out[k];
  else out[k] = merged;
  return out;
}

/** Credit scalar spears as MAA under an origin key. */
export function creditHostOriginMaa(
  origin: HostOriginMap | null | undefined,
  key: string,
  n: number,
): HostOriginMap {
  const qty = Math.max(0, Math.floor(n));
  if (qty <= 0) return cloneHostOrigin(origin);
  return creditHostOrigin(origin, key, stacksFromLegacy(qty));
}

/**
 * Peel up to `need` heads from one origin key (immutable).
 * Uses peelFromStacks order (maa first … catapult last).
 */
export function peelHostOriginKey(
  origin: HostOriginMap | null | undefined,
  key: string,
  need: number,
): { origin: HostOriginMap; taken: number; takenStacks: TroopStacks } {
  const k = (key && key.trim()) || HOST_ORIGIN_SEAT;
  const out = cloneHostOrigin(origin);
  const have = out[k] ?? emptyStacks();
  const { left, taken, takenStacks } = peelFromStacks(have, need);
  if (stacksTotal(left) <= 0) delete out[k];
  else out[k] = left;
  return { origin: out, taken, takenStacks };
}

/**
 * Peel up to `need` across the origin map.
 * Order: preferKeys (if given), then remaining keys sorted; within each key peelFromStacks.
 * Returns takenByKey for callers that must preserve ownership on the march token.
 */
export function peelHostOrigin(
  origin: HostOriginMap | null | undefined,
  need: number,
  preferKeys?: string[],
): {
  origin: HostOriginMap;
  taken: number;
  takenStacks: TroopStacks;
  takenByKey: HostOriginMap;
} {
  let remain = Math.max(0, Math.round(need));
  let out = cloneHostOrigin(origin);
  const takenByKey: HostOriginMap = {};
  let takenStacks = emptyStacks();
  let taken = 0;
  if (remain <= 0) {
    return { origin: out, taken: 0, takenStacks, takenByKey };
  }
  const seen = new Set<string>();
  const order: string[] = [];
  for (const k of preferKeys ?? []) {
    if (!k || seen.has(k)) continue;
    if (out[k]) {
      order.push(k);
      seen.add(k);
    }
  }
  for (const k of Object.keys(out).sort()) {
    if (seen.has(k)) continue;
    order.push(k);
    seen.add(k);
  }
  for (const k of order) {
    if (remain <= 0) break;
    const r = peelHostOriginKey(out, k, remain);
    out = r.origin;
    if (r.taken <= 0) continue;
    taken += r.taken;
    remain -= r.taken;
    takenStacks = addStacks(takenStacks, r.takenStacks);
    takenByKey[k] = addStacks(takenByKey[k] ?? emptyStacks(), r.takenStacks);
  }
  return { origin: out, taken, takenStacks, takenByKey };
}


/**
 * Scale a TroopStacks bag so stacksTotal === newTotal (battle survivors).
 * Proportional by type; largest-remainder; shortfall as MAA.
 */
export function scaleStacksToTotal(
  stacks: TroopStacks | null | undefined,
  newTotal: number,
): TroopStacks {
  const src = normalizeStacks(stacks ?? emptyStacks());
  const target = Math.max(0, Math.floor(newTotal));
  const oldTotal = stacksTotal(src);
  if (target <= 0) return emptyStacks();
  if (oldTotal <= 0) return stacksFromLegacy(target);
  if (target === oldTotal) return cloneStacks(src);
  if (target > oldTotal) {
    return addToStack(cloneStacks(src), "maa", target - oldTotal);
  }
  const scaled = emptyStacks();
  let gotTypes = 0;
  const typeFloor: { id: TroopTypeId; ideal: number; got: number }[] = [];
  for (const id of TROOP_TYPES) {
    const have = Math.max(0, Math.floor(src[id] ?? 0));
    if (have <= 0) continue;
    const ideal = (have / oldTotal) * target;
    const g = Math.floor(ideal);
    typeFloor.push({ id, ideal, got: g });
    gotTypes += g;
  }
  let tLeft = target - gotTypes;
  typeFloor.sort((a, b) => (b.ideal - b.got) - (a.ideal - a.got));
  for (const tf of typeFloor) {
    if (tLeft <= 0) break;
    tf.got += 1;
    tLeft -= 1;
  }
  for (const tf of typeFloor) {
    if (tf.got > 0) scaled[tf.id] = tf.got;
  }
  const st = stacksTotal(scaled);
  if (st < target) scaled.maa = Math.max(0, Math.floor(scaled.maa ?? 0)) + (target - st);
  return scaled;
}

/**
 * Scale every origin bag so totals sum to `newTotal` (battle survivors / losses).
 * Proportional by headcount; remainder distributed maa-first on largest bags.
 * Empty when newTotal ≤ 0.
 */
export function scaleHostOriginToTotal(
  origin: HostOriginMap | null | undefined,
  newTotal: number,
): HostOriginMap {
  const src = normalizeHostOrigin(origin);
  const target = Math.max(0, Math.floor(newTotal));
  const oldTotal = hostOriginTotal(src);
  if (target <= 0 || oldTotal <= 0) return {};
  if (target === oldTotal) return cloneHostOrigin(src);

  // First pass: floor proportional
  const out: HostOriginMap = {};
  const keys = Object.keys(src);
  let assigned = 0;
  const floors: { key: string; ideal: number; got: number; stacks: TroopStacks }[] = [];
  for (const k of keys) {
    const st = src[k]!;
    const bag = stacksTotal(st);
    const ideal = (bag / oldTotal) * target;
    const got = Math.floor(ideal);
    floors.push({ key: k, ideal, got, stacks: st });
    assigned += got;
  }
  let left = target - assigned;
  // Largest remainder for headcount
  floors.sort((a, b) => (b.ideal - b.got) - (a.ideal - a.got));
  for (const f of floors) {
    if (left <= 0) break;
    f.got += 1;
    left -= 1;
  }
  // Materialize each bag by peeling from a proportional clone of original stacks
  for (const f of floors) {
    if (f.got <= 0) continue;
    const bagTotal = stacksTotal(f.stacks);
    if (bagTotal <= 0) continue;
    if (f.got >= bagTotal && target >= oldTotal) {
      // growth path — rare; pad as MAA
      const extra = f.got - bagTotal;
      out[f.key] = extra > 0 ? addToStack(cloneStacks(f.stacks), "maa", extra) : cloneStacks(f.stacks);
      continue;
    }
    // shrink: peelFromStacks takes from front; we want keep proportion — scale each type
    const scaled = emptyStacks();
    let gotTypes = 0;
    const typeFloor: { id: TroopTypeId; ideal: number; got: number }[] = [];
    for (const id of TROOP_TYPES) {
      const have = Math.max(0, Math.floor(f.stacks[id] ?? 0));
      if (have <= 0) continue;
      const ideal = (have / bagTotal) * f.got;
      const g = Math.floor(ideal);
      typeFloor.push({ id, ideal, got: g });
      gotTypes += g;
    }
    let tLeft = f.got - gotTypes;
    typeFloor.sort((a, b) => (b.ideal - b.got) - (a.ideal - a.got));
    for (const tf of typeFloor) {
      if (tLeft <= 0) break;
      tf.got += 1;
      tLeft -= 1;
    }
    for (const tf of typeFloor) {
      if (tf.got > 0) scaled[tf.id] = tf.got;
    }
    // If still short (empty type list), dump MAA
    const st = stacksTotal(scaled);
    if (st < f.got) scaled.maa = Math.max(0, Math.floor(scaled.maa ?? 0)) + (f.got - st);
    if (stacksTotal(scaled) > 0) out[f.key] = scaled;
  }
  return out;
}

/**
 * Merge takenByKey credit map into origin (immutable).
 */
export function mergeHostOrigin(
  origin: HostOriginMap | null | undefined,
  addByKey: HostOriginMap | null | undefined,
): HostOriginMap {
  let out = cloneHostOrigin(origin);
  if (!addByKey) return out;
  for (const [k, st] of Object.entries(addByKey)) {
    out = creditHostOrigin(out, k, st);
  }
  return out;
}
